import React, { useMemo, useState } from 'react'
import {
  Dialog,
  Button,
  Flex,
  Text,
  TextField,
  RadioGroup,
  Box,
} from 'components/ds'
import Icon from 'components/ui/icon'
import apiClient from '@disco/api/server'
import { CancelSubscriptionNeedAssistanceLink } from './CancelSubscriptionNeedAssistanceLink'
import { parseServerError } from '@disco/utils/captureError'
import mixpanelClient from '@disco/api/mixpanel'
import { useDispatch } from 'hooks/use-redux'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import { useSelector } from 'react-redux'
import { getUser } from '@disco/state-management/selectors/user'

const REASON_CHOICES = [
  'I found another service that better suits my needs',
  'I’m no longer using DISCO',
  'It’s too expensive',
  'DISCO lacks the features I need',
  'Prefer not to say',
  'Other',
] as const

const FREE_TEXT_PLACEHOLDER_BY_REASON: {
  [key in typeof REASON_CHOICES[number]]: string
} = {
  'I found another service that better suits my needs': 'What service?*',
  'I’m no longer using DISCO': '',
  'It’s too expensive': '',
  'DISCO lacks the features I need': 'What features?*',
  'Prefer not to say': '',
  Other: 'Tell us more*',
}

type CancelSubscriptionModalStepTwoProps = {
  onNext: () => void
}

export const CancelSubscriptionModalStepTwo = ({
  onNext,
}: CancelSubscriptionModalStepTwoProps) => {
  const [currentReasonChoice, setCurrentReasonChoice] = useState('')
  const [error, setError] = useState(null)
  const [reasonFreeText, setReasonFreeText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const user = useSelector(getUser)

  const dispatch = useDispatch()

  const isValid = useMemo(() => {
    if (!currentReasonChoice) {
      return false
    }

    const hasFreeText = !!FREE_TEXT_PLACEHOLDER_BY_REASON[currentReasonChoice]

    if (hasFreeText) {
      return reasonFreeText.length > 0
    }

    return true
  }, [currentReasonChoice, reasonFreeText])

  const handleSubmit = async event => {
    event.preventDefault()
    setIsSubmitting(true)

    try {
      if (user.business.chargebee_subscription_id) {
        await apiClient.cancelChargebeeSubscription({
          reason_fixed_choices: currentReasonChoice,
          reason_free_text: reasonFreeText,
        })
      } else {
        await apiClient.cancelCurrentBusinessSubscription({
          reason_fixed_choices: currentReasonChoice,
          reason_free_text: reasonFreeText,
        })
      }

      await dispatch(loadBusinessSubscription())

      mixpanelClient.sendPureEvent('Cancel subscription', {
        Reason: currentReasonChoice,
      })
      onNext()
    } catch (error) {
      if (error.message) {
        setError(error.message)
      } else {
        setError(parseServerError(error))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  let errorNode = null
  if (error) {
    errorNode = (
      <Box mt="2">
        <Text color="red">{error}</Text>
      </Box>
    )
  }

  return (
    <>
      <Dialog.Title mt="0" mb="4">
        <Flex justify="between">
          <Text weight="medium" size="5">
            Can you let us know why?
          </Text>
          <Dialog.Close>
            <Button color="gray" variant="ghost">
              <Icon type="close" />
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Title>
      <form onSubmit={handleSubmit}>
        <Dialog.Description size="2" mb="2" mr="9">
          <Text weight={'medium'}>Please choose an option:</Text>
        </Dialog.Description>
        <RadioGroup.Root
          value={currentReasonChoice}
          onValueChange={val => {
            setReasonFreeText('')
            setCurrentReasonChoice(val)
          }}
          size={'3'}
        >
          <Flex gap="2" direction="column" width={'100%'} maxWidth="492px">
            {REASON_CHOICES.map(reasonChoice => (
              <React.Fragment key={reasonChoice}>
                <RadioGroup.Item value={reasonChoice}>
                  {reasonChoice}
                </RadioGroup.Item>
                {currentReasonChoice === reasonChoice &&
                  FREE_TEXT_PLACEHOLDER_BY_REASON[reasonChoice] && (
                    <TextField.Root
                      value={reasonFreeText}
                      onChange={evt => setReasonFreeText(evt.target.value)}
                      ml="28px"
                      placeholder={
                        FREE_TEXT_PLACEHOLDER_BY_REASON[reasonChoice]
                      }
                    />
                  )}
              </React.Fragment>
            ))}
          </Flex>
        </RadioGroup.Root>

        {errorNode}

        <Box mt="5">
          <CancelSubscriptionNeedAssistanceLink size="2" />
        </Box>

        <Flex gap="3" mt="4" justify="end">
          <Button
            type="submit"
            color="red"
            disabled={!isValid}
            loading={isSubmitting}
          >
            {'Cancel subscription'}
          </Button>
        </Flex>
      </form>
    </>
  )
}
